import { createContext, useContext, useMemo, type ReactNode } from 'react';

import { useAuth } from '@/auth/AuthContext';
import { createApiClient, type ApiClient } from './api-client';

const ApiContext = createContext<ApiClient | null>(null);

// Empty base URL = same origin. The api-client's paths already start with
// `/api/...` (e.g. `/api/me`), and Vite's dev server proxies `/api/*` to the
// BFF on :8080. In production the SPA is served from the same nginx that
// fronts the BFF, so same-origin works there too.
const DEFAULT_BASE_URL = '';

interface ApiProviderProps {
  children: ReactNode;
  /** Override the BFF base URL — primarily for tests. */
  baseUrl?: string;
  /**
   * Inject a fully-built client. Test seam: lets specs render the SPA with
   * a stub `ApiClient` and bypass the auth/baseUrl plumbing entirely.
   */
  client?: ApiClient;
}

/**
 * Wraps the SPA in a single typed `ApiClient` bound to the current Auth0
 * token resolver. Re-creates the client whenever auth state changes so the
 * `getToken` closure always sees the latest Auth0 SDK state.
 */
export function ApiProvider({ children, baseUrl = DEFAULT_BASE_URL, client }: ApiProviderProps) {
  const { isConfigured, isAuthenticated, getToken } = useAuth();

  const resolved = useMemo<ApiClient>(
    () =>
      client ??
      createApiClient({
        baseUrl,
        getToken: async () => {
          if (!isConfigured || !isAuthenticated) {
            throw new Error('Not authenticated');
          }
          return getToken();
        },
      }),
    [client, baseUrl, isConfigured, isAuthenticated, getToken],
  );

  return <ApiContext.Provider value={resolved}>{children}</ApiContext.Provider>;
}

export function useApi(): ApiClient {
  const ctx = useContext(ApiContext);
  if (!ctx) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return ctx;
}
